import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  MessageSquare, Send, Plus, Bot, User, Loader2, Clock, 
  AlertTriangle, ChevronRight, Trash2, X, RefreshCw,
  FileText, ArrowUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useTimezone } from '@/contexts/TimezoneContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIHelperPage = () => {
  const { user } = useAuth();
  const { formatTime: formatTimeWithTimezone, formatDate: formatDateWithTimezone } = useTimezone();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [departments, setDepartments] = useState([]);
  
  // New session dialog
  const [newSessionDialog, setNewSessionDialog] = useState(false);
  const [newSessionDept, setNewSessionDept] = useState('');
  
  // Escalation dialog
  const [escalationDialog, setEscalationDialog] = useState(false);
  const [escalationReason, setEscalationReason] = useState('');
  const [escalationPriority, setEscalationPriority] = useState('normal');
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-helper/sessions`);
      setSessions(res.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  }, []);

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${API}/rbac/departments`);
      setDepartments(res.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchSessions(), fetchDepartments()]);
      setLoading(false);
    };
    init();
  }, [fetchSessions]);

  // Fetch messages for active session
  const fetchMessages = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/ai-helper/sessions/${sessionId}/messages`);
      setMessages(res.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Erro ao carregar mensagens');
    }
  };

  // Select session
  const handleSelectSession = async (session) => {
    setActiveSession(session);
    await fetchMessages(session.id);
    inputRef.current?.focus();
  };

  // Create new session
  const handleCreateSession = async () => {
    try {
      const res = await axios.post(`${API}/ai-helper/sessions`, {
        department_id: newSessionDept === 'all' ? null : (newSessionDept || null),
        title: null
      });
      
      setSessions(prev => [res.data, ...prev]);
      setActiveSession(res.data);
      setMessages([]);
      setNewSessionDialog(false);
      setNewSessionDept('');
      
      toast.success('Nova conversa iniciada');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Erro ao criar conversa');
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!newMessage.trim() || !activeSession || sendingMessage) return;
    
    const messageContent = newMessage.trim();
    setNewMessage('');
    setSendingMessage(true);
    
    // Optimistic update
    const tempUserMsg = {
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    try {
      const res = await axios.post(
        `${API}/ai-helper/sessions/${activeSession.id}/messages`,
        { content: messageContent }
      );
      
      // Replace optimistic message with real response
      setMessages(prev => [
        ...prev.slice(0, -1),
        res.data.message,
        res.data.assistant_response
      ]);
      
      // Update session in list
      setSessions(prev => prev.map(s => 
        s.id === activeSession.id 
          ? { ...s, message_count: s.message_count + 2, last_message: messageContent }
          : s
      ));
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erro ao enviar mensagem');
      // Remove optimistic message
      setMessages(prev => prev.slice(0, -1));
      setNewMessage(messageContent);
    } finally {
      setSendingMessage(false);
    }
  };

  // Close session
  const handleCloseSession = async () => {
    if (!activeSession) return;
    
    try {
      await axios.post(`${API}/ai-helper/sessions/${activeSession.id}/close`);
      toast.success('Conversa encerrada');
      
      setSessions(prev => prev.map(s =>
        s.id === activeSession.id ? { ...s, status: 'closed' } : s
      ));
      setActiveSession(null);
      setMessages([]);
    } catch (error) {
      console.error('Error closing session:', error);
      toast.error('Erro ao encerrar conversa');
    }
  };

  // Create escalation
  const handleCreateEscalation = async () => {
    if (!activeSession || !escalationReason.trim()) return;
    
    try {
      await axios.post(`${API}/ai-helper/escalations`, {
        session_id: activeSession.id,
        reason: escalationReason,
        priority: escalationPriority
      });
      
      toast.success('Conversa escalada para um gerente');
      setEscalationDialog(false);
      setEscalationReason('');
      
      setSessions(prev => prev.map(s =>
        s.id === activeSession.id ? { ...s, status: 'escalated' } : s
      ));
      setActiveSession(prev => ({ ...prev, status: 'escalated' }));
    } catch (error) {
      console.error('Error creating escalation:', error);
      toast.error('Erro ao escalar conversa');
    }
  };

  const formatTime = (timestamp) => {
    return formatTimeWithTimezone(timestamp, { omitSeconds: true });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Hoje';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ontem';
    }
    return formatDateWithTimezone(timestamp, { day: '2-digit', month: '2-digit' });
  };

  if (loading) {
    return (
      <div className="h-full min-h-0 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="page-container-fill md:flex-row gap-4" data-testid="ai-helper-page">
      {/* Sessions List */}
      <div className="w-full md:w-80 md:min-h-0 flex flex-col bg-card rounded-lg border">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">AI Helper</h2>
          </div>
          <Button 
            size="sm" 
            onClick={() => setNewSessionDialog(true)}
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nova
          </Button>
        </div>
        
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {sessions.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm py-8">
                Nenhuma conversa ainda
              </p>
            ) : (
              sessions.map((session) => (
                <button
                  key={session.id}
                  onClick={() => handleSelectSession(session)}
                  className={cn(
                    "w-full text-left p-3 rounded-lg transition-colors",
                    activeSession?.id === session.id
                      ? "bg-primary/10 border border-primary/20"
                      : "hover:bg-muted"
                  )}
                  data-testid={`session-${session.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-sm">
                        {session.title || 'Nova conversa'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {session.last_message || 'Sem mensagens'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(session.updated_at)}
                      </span>
                      {session.status === 'escalated' && (
                        <Badge variant="destructive" className="text-xs">
                          Escalado
                        </Badge>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className="flex-1 min-h-0 min-w-0 flex flex-col bg-card rounded-lg border overflow-hidden">
        {activeSession ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b flex items-center justify-between bg-muted/30">
              <div>
                <h3 className="font-medium">{activeSession.title || 'Conversa'}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {activeSession.department_name && (
                    <>
                      <Badge variant="outline" className="text-xs">
                        {activeSession.department_name}
                      </Badge>
                      <span>•</span>
                    </>
                  )}
                  <span>{activeSession.message_count} mensagens</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {activeSession.status === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEscalationDialog(true)}
                    className="text-orange-600 border-orange-200 hover:bg-orange-50"
                    data-testid="escalate-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Escalar
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseSession}
                  data-testid="close-chat-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4 max-w-3xl mx-auto">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <Bot className="w-12 h-12 mx-auto text-primary/50 mb-4" />
                    <p className="text-muted-foreground">
                      Olá! Sou o Octopus AI Helper.
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Como posso ajudá-lo hoje?
                    </p>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "flex gap-3",
                        msg.role === 'user' ? "justify-end" : "justify-start"
                      )}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <div
                        className={cn(
                          "max-w-[80%] rounded-2xl px-4 py-2",
                          msg.role === 'user'
                            ? "bg-primary text-primary-foreground rounded-br-md"
                            : "bg-muted rounded-bl-md"
                        )}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <span className={cn(
                          "text-xs mt-1 block",
                          msg.role === 'user' ? "text-primary-foreground/70" : "text-muted-foreground"
                        )}>
                          {formatTime(msg.timestamp)}
                        </span>
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  ))
                )}
                
                {sendingMessage && (
                  <div className="flex gap-3 justify-start">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">Pensando...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            {activeSession.status === 'active' && (
              <form onSubmit={handleSendMessage} className="p-4 border-t bg-muted/30">
                <div className="flex gap-2 max-w-3xl mx-auto">
                  <Textarea
                    ref={inputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Digite sua pergunta..."
                    className="min-h-[44px] max-h-32 resize-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={sendingMessage}
                    data-testid="chat-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={!newMessage.trim() || sendingMessage}
                    className="h-11 px-4"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </form>
            )}
            
            {activeSession.status === 'escalated' && (
              <div className="p-4 border-t bg-orange-50 text-center">
                <p className="text-sm text-orange-700">
                  Esta conversa foi escalada para um gerente.
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground">
                Selecione uma conversa
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                ou inicie uma nova para começar
              </p>
              <Button
                className="mt-4"
                onClick={() => setNewSessionDialog(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Conversa
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* New Session Dialog */}
      <Dialog open={newSessionDialog} onOpenChange={setNewSessionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Conversa</DialogTitle>
            <DialogDescription>
              Inicie uma nova conversa com o AI Helper
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Departamento (opcional)</Label>
              <Select value={newSessionDept} onValueChange={setNewSessionDept}>
                <SelectTrigger data-testid="dept-select">
                  <SelectValue placeholder="Todos os departamentos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os departamentos</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Selecione um departamento para focar as respostas nos SOPs daquela área
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewSessionDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateSession} data-testid="create-session-btn">
              <MessageSquare className="w-4 h-4 mr-2" />
              Iniciar Conversa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalation Dialog */}
      <Dialog open={escalationDialog} onOpenChange={setEscalationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escalar para Gerente</DialogTitle>
            <DialogDescription>
              Descreva o motivo da escalação para que um gerente possa ajudá-lo
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Motivo da Escalação *</Label>
              <Textarea
                value={escalationReason}
                onChange={(e) => setEscalationReason(e.target.value)}
                placeholder="Descreva por que você precisa de ajuda de um gerente..."
                rows={3}
                data-testid="escalation-reason"
              />
            </div>
            <div className="space-y-2">
              <Label>Prioridade</Label>
              <Select value={escalationPriority} onValueChange={setEscalationPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEscalationDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateEscalation}
              disabled={!escalationReason.trim()}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="submit-escalation-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Escalar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIHelperPage;
